import { useEffect, useState } from "react";

import { Box } from "@mui/material";

import { useArrow } from "./ArrowContext";

import { isTest } from "@/common/constants/env";
import { DashboardIcon } from "@/dashboard-design-system/Icon/DashboardIcon";
import { ArrowRight } from "@/icons/outlined";

type ArrowPreviewProps = {
  iframeRef: React.RefObject<HTMLIFrameElement | null>;
  currentStepIndex: number;
};

export const ArrowPreview: React.FC<ArrowPreviewProps> = ({ iframeRef, currentStepIndex: currentStep }) => {
  const { arrowTop, refreshArrowPosition, isArrowVisible, hideArrow, transitionSpeed, userHasScrolled } = useArrow();
  const [isStepChanging, setIsStepChanging] = useState(false);

  useEffect(() => {
    if (iframeRef.current) {
      refreshArrowPosition();
    }
  }, [refreshArrowPosition, iframeRef]);

  // Hide arrow when the user scrolls into the preview section
  useEffect(() => {
    let cleanup: (() => void) | undefined;

    const timeoutId = setTimeout(() => {
      const iframeDocument = iframeRef.current?.contentDocument;
      if (!iframeDocument) {
        return;
      }

      const handleUserScroll = () => {
        userHasScrolled.current = true;
        hideArrow("slow");
      };

      iframeDocument.addEventListener("wheel", handleUserScroll, { passive: true });
      iframeDocument.addEventListener("touchmove", handleUserScroll, { passive: true });

      cleanup = () => {
        iframeDocument.removeEventListener("wheel", handleUserScroll);
        iframeDocument.removeEventListener("touchmove", handleUserScroll);
      };
    }, 500);

    return () => {
      clearTimeout(timeoutId);
      if (cleanup) {
        cleanup();
      }
    };
  }, [iframeRef, hideArrow, userHasScrolled]);

  // Add effect to handle step changes
  useEffect(() => {
    setIsStepChanging(true);
    const timeoutId = setTimeout(() => {
      setIsStepChanging(false);
    }, 800);

    return () => clearTimeout(timeoutId);
  }, [currentStep]);

  return (
    <>
      <style>
        {`
        @keyframes arrow-bounce {
          0%, 60% {
            transform: translateX(-100%);
          }
          70% {
            transform: translateX(calc(-100% - 20px));
          }
          80% {
            transform: translateX(-100%);
          }
        }
      `}
      </style>

      <Box
        sx={{
          position: "absolute",
          top: arrowTop,
          left: "-10px",
          zIndex: 2,
          pointerEvents: "none",
          transform: "translateX(-100%)",
          animation: "arrow-bounce 3s ease-in-out infinite",
          display: isStepChanging ? "none" : "block", // Hide the arrow when the step is changing, not using opacity to avoid flickering
          opacity: isArrowVisible && arrowTop ? 1 : 0,
          transition: `opacity ${transitionSpeed === "slow" ? "0.5s" : "0.2s"} ease-in-out`,

          // Disable animation and transition in test environment to avoid flakyness in snapshots testing
          ...(isTest && {
            display: "none",
          }),
        }}
        data-test="arrow"
      >
        <DashboardIcon size="large" vibe="text-neutral-quiet">
          <ArrowRight />
        </DashboardIcon>
      </Box>
    </>
  );
};
